<script setup lang="ts">
/**
 * Root App component.
 *
 * @component App
 */

import { onMounted, computed } from 'vue'
import { useAuthStore, useUiStore } from '@/stores'
import { initDatabase } from '@/api/laboratory'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'

const API_URL = 'https://api.cylian.org'

const auth = useAuthStore()
const ui = useUiStore()

// Only render router-view after auth is initialized
const ready = computed(() => auth.initialized)

onMounted(async () => {
  // Initialize theme
  ui.initTheme()

  // Initialize API client
  initDatabase(API_URL)

  // Initialize auth (handles OAuth callback and token restoration)
  await auth.init(API_URL)
})
</script>

<template>
  <!-- Layout Header -->
  <layout-header id="layout-header" role="banner"><header id="theme-header">
    <AppHeader />
  </header></layout-header>

  <!-- Router View (each view defines its own layout) - wait for auth -->
  <template v-if="ready">
    <router-view />
  </template>
  <template v-else>
    <layout-main id="layout-main"><main id="theme-main" role="main">
      <LoadingSpinner text="Initialisation..." />
    </main></layout-main>
  </template>

  <!-- Layout Footer -->
  <layout-footer id="layout-footer" role="contentinfo"><footer id="theme-footer">
    <AppFooter />
  </footer></layout-footer>
</template>

<style>
/* Data table styles for sections */
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.data-table th,
.data-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--color-border, #e5e5e5);
}

.data-table tbody th {
  text-align: left;
  font-weight: 500;
  color: var(--color-text-secondary, #666);
  width: 40%;
}

.data-table tbody td {
  text-align: right;
}

.data-table tbody tr:hover {
  background: var(--color-background-soft, #f5f5f5);
}

/* Multi-column tables (with thead) */
.data-table.multi-col thead th {
  font-weight: 600;
  color: var(--color-text-secondary, #666);
  background: var(--color-background-soft, #f5f5f5);
  text-align: right;
}

.data-table.multi-col thead th:first-child {
  text-align: left;
}

.data-table.multi-col tbody th {
  width: auto;
}
</style>
